/**
 * Phase 6.0 — Visit Action-Pending direction matrix + intent metadata.
 *
 * Mirrors the server-side enforcement in `visit_action_pending_service`.
 * Frontend uses these to filter the to_role / intent pickers in
 * `ActionPendingDialog`. Source of truth is still the backend; this is
 * a UX-side allowlist for sensible defaults + hint text.
 */

export const INTENTS = {
  COLLECT_PAYMENT: 'COLLECT_PAYMENT',
  CLOSE_AFTER_PAYMENT: 'CLOSE_AFTER_PAYMENT',
  ASSIGN_BACK_TO_ME: 'ASSIGN_BACK_TO_ME',
  ASSIGN_BACK_TO_DOCTOR: 'ASSIGN_BACK_TO_DOCTOR',
  CONSULT: 'CONSULT',
  PERFORM_NURSING_TASK: 'PERFORM_NURSING_TASK',
  HANDOVER: 'HANDOVER',
  REFERRAL_TO_DOCTOR: 'REFERRAL_TO_DOCTOR',
};

export const RESOLUTIONS = {
  RESOLVED: 'RESOLVED',
  FORWARD: 'FORWARD',
  CANCEL: 'CANCEL',
};

/**
 * Allowed (from_role → to_role → [intents]).
 * Doctor → Nurse is intentionally absent — doctors delegate procedures
 * via the existing Phase-1b sitting-handoff flow, not via visit-level
 * action-pending.
 */
export const DIRECTION_MATRIX = {
  RECEPTIONIST: {
    RECEPTIONIST: [INTENTS.HANDOVER],
    DOCTOR: [INTENTS.CONSULT],
    NURSE: [INTENTS.PERFORM_NURSING_TASK],
  },
  DOCTOR: {
    RECEPTIONIST: [
      INTENTS.COLLECT_PAYMENT,
      INTENTS.CLOSE_AFTER_PAYMENT,
      INTENTS.ASSIGN_BACK_TO_ME,
      // Phase 3.1 — when the bypass conditions don't all hold, the
      // referral parks with the Receptionist for payment + forward.
      INTENTS.REFERRAL_TO_DOCTOR,
    ],
  },
  NURSE: {
    RECEPTIONIST: [
      INTENTS.COLLECT_PAYMENT,
      INTENTS.ASSIGN_BACK_TO_DOCTOR,
    ],
  },
};

export const INTENTS_REQUIRING_COMMENT = new Set([
  INTENTS.COLLECT_PAYMENT,
  INTENTS.CLOSE_AFTER_PAYMENT,
  INTENTS.ASSIGN_BACK_TO_ME,
  INTENTS.ASSIGN_BACK_TO_DOCTOR,
  INTENTS.REFERRAL_TO_DOCTOR,
]);

export const INTENT_LABELS = {
  [INTENTS.COLLECT_PAYMENT]: 'Collect payment',
  [INTENTS.CLOSE_AFTER_PAYMENT]: 'Collect payment & close visit',
  [INTENTS.ASSIGN_BACK_TO_ME]: 'Collect payment, send back to me',
  [INTENTS.ASSIGN_BACK_TO_DOCTOR]: 'Collect payment, send back to doctor',
  [INTENTS.CONSULT]: 'Consult',
  [INTENTS.PERFORM_NURSING_TASK]: 'Perform nursing task',
  [INTENTS.HANDOVER]: 'Handover (shift change etc.)',
  [INTENTS.REFERRAL_TO_DOCTOR]: 'Refer to another doctor',
};

export const ROLE_LABELS = {
  RECEPTIONIST: 'Reception',
  DOCTOR: 'Doctor',
  NURSE: 'Nurse',
  ADMIN: 'Admin',
};

/**
 * Role-tinted chip palette for `[⌛ Action: <role>]`.
 * Day-mode tints are SOLID per the contrast rule in CLAUDE.md.
 */
export const ROLE_CHIP_PALETTE = {
  RECEPTIONIST: {
    bg: 'bg-amber-100 dark:bg-amber-900/40',
    border: 'border-amber-400 dark:border-amber-700',
    text: 'text-amber-900 dark:text-amber-100',
  },
  DOCTOR: {
    bg: 'bg-indigo-100 dark:bg-indigo-900/40',
    border: 'border-indigo-400 dark:border-indigo-700',
    text: 'text-indigo-900 dark:text-indigo-100',
  },
  NURSE: {
    bg: 'bg-violet-100 dark:bg-violet-900/40',
    border: 'border-violet-400 dark:border-violet-700',
    text: 'text-violet-900 dark:text-violet-100',
  },
};

export const FOLLOWUP_CHIP_PALETTE = {
  bg: 'bg-emerald-100 dark:bg-emerald-900/40',
  border: 'border-emerald-400 dark:border-emerald-700',
  text: 'text-emerald-900 dark:text-emerald-100',
};

/**
 * Phase 3.1 — Receptionist Action-Pending tab intent chips.
 * Solid day tints + dark-mode tints at /40 alpha per the contrast rule.
 * Categorises intents into Payment / Referral / Nurse-action groups so
 * the lane reads at a glance.
 */
export const INTENT_CATEGORY = {
  [INTENTS.COLLECT_PAYMENT]: 'PAYMENT',
  [INTENTS.CLOSE_AFTER_PAYMENT]: 'PAYMENT',
  [INTENTS.ASSIGN_BACK_TO_ME]: 'PAYMENT',
  [INTENTS.ASSIGN_BACK_TO_DOCTOR]: 'PAYMENT',
  [INTENTS.CONSULT]: 'OTHER',
  [INTENTS.PERFORM_NURSING_TASK]: 'NURSE',
  [INTENTS.HANDOVER]: 'OTHER',
  [INTENTS.REFERRAL_TO_DOCTOR]: 'REFERRAL',
};

export const INTENT_CATEGORY_PALETTE = {
  PAYMENT: {
    bg: 'bg-amber-100 dark:bg-amber-900/40',
    border: 'border-amber-400 dark:border-amber-700',
    text: 'text-amber-900 dark:text-amber-100',
    label: 'Payment',
    icon: '🟡',
  },
  REFERRAL: {
    bg: 'bg-orange-100 dark:bg-orange-900/40',
    border: 'border-orange-400 dark:border-orange-700',
    text: 'text-orange-900 dark:text-orange-100',
    label: 'Referral',
    icon: '🟠',
  },
  NURSE: {
    bg: 'bg-blue-100 dark:bg-blue-900/40',
    border: 'border-blue-400 dark:border-blue-700',
    text: 'text-blue-900 dark:text-blue-100',
    label: 'Nurse',
    icon: '🔵',
  },
  OTHER: {
    bg: 'bg-slate-100 dark:bg-slate-800',
    border: 'border-slate-400 dark:border-slate-600',
    text: 'text-slate-800 dark:text-slate-100',
    label: 'Other',
    icon: '⚪',
  },
};
